import { assignIn, concat, get } from 'lodash'
import * as log from 'loglevel'

import {
  DELETE_ALL_NOTIFICATION,
  DELETE_NOTIFICATION,
  DELETE_VAPID_PUBLIC_KEY,
  GET_NOTIFICATIONS,
  SET_NOTIFICATIONS,
  SET_REGISTERING_REQUEST_STATUS,
  UPDATE_IS_SW_INSTALLED,
  UPDATE_VAPID_PUBLIC_KEY
} from 'stores/types/notification'

let urlBase64ToUint8Array = base64String => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

const state = {
  vapidPublicKey: null,
  isSWInstalled: null,
  notifications: [],
  isRegisteringWithAPI: false
}

// getters
const getters = {
  vapidPublicKey(state, getters, rootState) {
    if (state.vapidPublicKey === null) {
      if (rootState.localStore.get('vapidPublicKey') === undefined) {
        return null
      } else {
        state.vapidPublicKey = rootState.localStore.get('vapidPublicKey')
        return state.vapidPublicKey
      }
    } else {
      return state.vapidPublicKey
    }
  },
  isSWInstalled: state => state.isSWInstalled,
  serviceWorkerPath() {
    return '/sw.js'
  },
  notifications: state => state.notifications
}

function getOptions(ctx) {
  return {
    headers: {
      Authorization: ctx.rootState.user.token
    }
  }
}

function getSubscriptionPayload(subscription) {
  const p256dh = subscription.getKey('p256dh')
  const auth = subscription.getKey('auth')
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: p256dh
        ? btoa(String.fromCharCode.apply(null, new Uint8Array(p256dh)))
        : null,
      auth: auth
        ? btoa(String.fromCharCode.apply(null, new Uint8Array(auth)))
        : null
    }
  }
}

const actions = {
  /**
   * Delete all notifications in indexedDB
   * @param   {Object} ctx
   * @returns {Promise}
   */
  async [DELETE_ALL_NOTIFICATION](ctx) {
    try {
      const notification = ctx.rootState.db.notifications
      const currentUserId = ctx.getters.currentUser.id
      const notifications = await notification
        .where('userId')
        .equals(currentUserId)
      await notifications.delete()
      await ctx.dispatch(GET_NOTIFICATIONS)
    } catch (err) {
      log.error(err)
    }
  },
  /**
   * Get notifications from indexedDB
   * @param   {*} ctx
   * @returns {Promise<Array>} - returns a list of notifications
   */
  async [GET_NOTIFICATIONS](ctx) {
    try {
      const currentUserId = ctx.getters.currentUser.id
      const notification = ctx.rootState.db.notifications
      const notifications = await notification
        .where('userId')
        .equals(currentUserId)
        .reverse()
        .sortBy('createdAt')
      ctx.commit(SET_NOTIFICATIONS, notifications)
      return notifications
    } catch (err) {
      log.error(err)
    }
  },
  async enableNotification(ctx) {
    const updates = {
      notificationEnabled: true
    }
    await ctx.dispatch('updateCurrentUser', updates)
  },
  async disableNotification(ctx) {
    const updates = {
      notificationEnabled: false
    }
    await ctx.dispatch('updateCurrentUser', updates)
    await ctx.dispatch('revokeSubscription')

    ctx.commit(SET_NOTIFICATIONS, [])
  },
  async setVapidPublicKey(ctx, vapidPublicKey) {
    await ctx.rootState.localStore.set('vapidPublicKey', vapidPublicKey)
    await ctx.commit(UPDATE_VAPID_PUBLIC_KEY, vapidPublicKey)
  },
  deleteVapidPublicKey(ctx) {
    ctx.rootState.localStore.remove('vapidPublicKey')
    ctx.commit(DELETE_VAPID_PUBLIC_KEY)
  },
  async subscribe(ctx) {
    const { notificationEnabled } = ctx.getters.user
    if (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      Notification
    ) {
      const vapidPublicKey = ctx.rootState.localStore.get('vapidPublicKey')
      const { permission } = Notification
      if (notificationEnabled === true) {
        try {
          if (permission === 'denied') {
            await ctx.dispatch('disableNotification')
            return
          }

          if (permission === 'default') {
            let response = await Notification.requestPermission()
            if (response === 'denied') {
              await ctx.dispatch('disableNotification')
              return
            } else {
              await ctx.dispatch('enableNotification')
            }
          }

          if (!vapidPublicKey) {
            throw new Error('vapid public key should not be null')
          }

          let subscription = await ctx.dispatch('getCurrentSubscription')
          let isServiceWorkerRegistered =
            (await navigator.serviceWorker.getRegistration()) &&
            ctx.state.isSWInstalled
          if (!isServiceWorkerRegistered) {
            log.warn('Service worker is not registered')
            log.info('Try to register service worker...')
            await ctx.dispatch('installServiceWorker')
            isServiceWorkerRegistered =
              (await navigator.serviceWorker.getRegistration()) &&
              ctx.state.isSWInstalled
            if (!isServiceWorkerRegistered) {
              throw new Error('Failed to register service worker')
            } else {
              subscription = await ctx.dispatch('getCurrentSubscription')
            }
          }

          if (subscription) {
            let next = await ctx.dispatch('updateSubscription')
            if (next) {
              ctx.dispatch('listenToMessageChannel')
              ctx.dispatch(GET_NOTIFICATIONS)
            }
          } else {
            await ctx.dispatch('getNewPushSubscription')
            let next = await ctx.dispatch('createSubscription')
            if (next) {
              ctx.dispatch('listenToMessageChannel')
              ctx.dispatch(GET_NOTIFICATIONS)
            }
          }
        } catch (error) {
          log.error(error)
          let statusCode = get(error, 'response.status', null)
          if (statusCode === 404) {
            await ctx.dispatch('createSubscription')
            ctx.dispatch('listenToMessageChannel')
            ctx.dispatch(GET_NOTIFICATIONS)
          } else if (error.message === 'vapid public key should not be null') {
            /* disable notification if no public key provided, and popout error message */
            await ctx.dispatch('disableNotification')
            throw new Error('Failed to enable notification')
          }
        }
      }
    } else {
      await ctx.dispatch('disableNotification')
    }
  },
  async listenToMessageChannel(ctx) {
    let messageListener = event => {
      const data = event.data
      if (data.NEW_NOTIFICATION) {
        ctx.dispatch(GET_NOTIFICATIONS)
      }
    }
    await navigator.serviceWorker.removeEventListener(
      'message',
      messageListener
    )
    await navigator.serviceWorker.addEventListener('message', messageListener)
  },
  async createSubscription(ctx) {
    if (ctx.state.isRegisteringWithAPI) {
      return false
    }
    ctx.commit(SET_REGISTERING_REQUEST_STATUS, true)
    let options = {}
    options.options = getOptions(ctx)
    const currentUserId = ctx.getters.currentUser.id
    try {
      let registration = await navigator.serviceWorker.getRegistration()
      if (!registration) {
        await ctx.dispatch('installServiceWorker')
        registration = await navigator.serviceWorker.getRegistration()
      }
      let subscription = await registration.pushManager.getSubscription()
      let payload
      if (subscription) {
        payload = assignIn(getSubscriptionPayload(subscription), {
          userId: currentUserId
        })
      } else {
        throw new Error('Failed to register service worker')
      }
      if (payload) {
        options.payload = payload
      }
      let data = await ctx.rootState.api.createSubscription(options)
      ctx.commit(SET_REGISTERING_REQUEST_STATUS, false)
      return data
    } catch (err) {
      log.error(err)
      ctx.commit(SET_REGISTERING_REQUEST_STATUS, false)
    }
  },
  async updateSubscription(ctx) {
    if (ctx.state.isRegisteringWithAPI) {
      return
    }
    ctx.commit(SET_REGISTERING_REQUEST_STATUS, true)
    let options = {}
    options.options = getOptions(ctx)
    const currentUserId = ctx.getters.currentUser.id
    try {
      let registration = await navigator.serviceWorker.getRegistration()
      if (!registration) {
        await ctx.dispatch('installServiceWorker')
        registration = await navigator.serviceWorker.getRegistration()
      }
      let subscription = await registration.pushManager.getSubscription()
      let payload
      if (subscription) {
        payload = assignIn(getSubscriptionPayload(subscription), {
          userId: currentUserId
        })
      } else {
        throw new Error('Failed to register service worker')
      }
      if (payload) {
        options.payload = payload
      }
      let data = ctx.rootState.api.updateSubscription(options)
      ctx.commit(SET_REGISTERING_REQUEST_STATUS, false)
      return data
    } catch (err) {
      log.error(err)
      ctx.commit(SET_REGISTERING_REQUEST_STATUS, false)
    }
  },
  async revokeSubscription(ctx) {
    let options = {}
    options.options = getOptions(ctx)
    try {
      let registration = await navigator.serviceWorker.getRegistration()
      if (registration) {
        let subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          await subscription.unsubscribe()
        }
      }
      await ctx.rootState.api.revokeSubscription(options)
    } catch (err) {
      log.error(err)
    }
  },
  async installServiceWorker(ctx) {
    if (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      Notification
    ) {
      try {
        await navigator.serviceWorker.register(ctx.getters.serviceWorkerPath)
        ctx.commit(UPDATE_IS_SW_INSTALLED, true)
      } catch (err) {
        console.error(err)
        ctx.commit(UPDATE_IS_SW_INSTALLED, false)
      }
    } else {
      ctx.commit(UPDATE_IS_SW_INSTALLED, false)
    }
  },
  async getNewPushSubscription(ctx) {
    const vapidPublicKey = ctx.rootState.localStore.get('vapidPublicKey')
    const options = {
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    }
    try {
      let registration = await navigator.serviceWorker.getRegistration()
      if (!registration) {
        await ctx.dispatch('installServiceWorker')
        registration = await navigator.serviceWorker.getRegistration()
      }
      await registration.pushManager.subscribe(options)
    } catch (error) {
      log.error(error)
    }
  },

  async getCurrentSubscription(ctx) {
    try {
      let registration = await navigator.serviceWorker.getRegistration()
      if (registration) {
        let subscription = await registration.pushManager.getSubscription()
        return subscription
      }
    } catch (err) {
      log.error(err)
    }
  },
  // /**
  //  * Insert notification into indexed db
  //  * @param   {Object}        ctx
  //  * @param   {Object}        data                        - notification message from api
  //  * @param   {String}        data.data.id                - notification message id
  //  * @param   {Object}        data.data.message           - notification message info
  //  * @param   {String}        data.data.message.content   - content should be displayed in notification
  //  * @param   {String}        data.data.message.createdAt - notification created at timestamp
  //  * @param   {Array<Object>} data.data.message.events    - notification events
  //  * @param   {String}        data.data.message.title     - notification title
  //  * @param   {String}        data.data.event.type        - notification type
  //  * @param   {String}        data.userId                 - user who should receive this notification message
  //  * @returns {Promise}                           - returns indexed db record
  //  */
  // async [INSERT_NOTIFICATION](ctx, data) {
  //   try {
  //     const { type } = data
  //     const { userId, message, id } = data.data
  //     const { content, createdAt, events, title } = message
  //     const { payload } = events[0]
  //     const user = payload.user
  //     const pageId = payload.chunk.root
  //       ? payload.chunk.root.id
  //       : payload.chunk.id
  //     const redirect = {
  //       name: 'problemPage',
  //       params: {
  //         id: pageId
  //       }
  //     }
  //     const record = {
  //       createdAt: moment(createdAt).valueOf(),
  //       id,
  //       message: content,
  //       redirect,
  //       title,
  //       type,
  //       user,
  //       userId
  //     }
  //     await ctx.rootState.db.notifications.put(record)
  //     // update notifications in Vuex
  //     await ctx.dispatch(GET_NOTIFICATIONS)
  //   } catch (err) {
  //     log.error(err)
  //   }
  // },
  /**
   * Delete notification in indexedDB
   * @param   {Object} ctx
   * @param   {String} id  - notification id
   * @returns {Promise}
   */
  async [DELETE_NOTIFICATION](ctx, id) {
    try {
      const notification = ctx.rootState.db.notifications
      await notification.delete(id)
      await ctx.dispatch(GET_NOTIFICATIONS)
    } catch (err) {
      log.error(err)
    }
  }
}

const mutations = {
  UPDATE_VAPID_PUBLIC_KEY(state, vapidPublicKey) {
    state.vapidPublicKey = vapidPublicKey
  },
  DELETE_VAPID_PUBLIC_KEY(state) {
    state.vapidPublicKey = null
  },
  UPDATE_IS_SW_INSTALLED(state, change) {
    state.isSWInstalled = change
  },
  SET_NOTIFICATIONS(state, notifications) {
    state.notifications = concat([], notifications)
  },
  SET_REGISTERING_REQUEST_STATUS(state, status) {
    state.isRegisteringWithAPI = status
  }
}

export default {
  getters,
  state,
  mutations,
  actions
}
